import { useEffect, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { ClientSelector } from "@/components/capital/ClientSelector";
import { CapitalOverview } from "@/components/capital/CapitalOverview";
import { RenewalAssessment } from "@/components/capital/RenewalAssessment";
import { SiteAnalysis } from "@/components/capital/SiteAnalysis";
import { AssetClassForecast } from "@/components/capital/AssetClassForecast";
import { TenYearForecast } from "@/components/capital/TenYearForecast";
import { Scenarios } from "@/components/capital/Scenarios";
import { BoardReport } from "@/components/capital/BoardReport";
import { MethodologyTab } from "@/components/capital/MethodologyTab";
import { PriorYearComparison } from "@/components/capital/PriorYearComparison";
import { AskAssetAI, AIPanel } from "@/components/capital/AskAssetAI";
import AssetRegister from "@/components/capital/AssetRegister";
import AssetPortfolio from "@/components/capital/AssetPortfolio";
import { useCapital } from "@/context/CapitalContext";
import { usePermissions } from "@/context/PermissionsContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { TID } from "@/constants/testIds";

const HORIZONS = {
  immediate: "Immediate Attention",
  m12: "12 Month Plan",
  m24: "24 Month Plan",
  monitor: "Monitor",
};

export default function CapitalPlanningPage() {
  const { client, loading } = useCapital();
  const { role } = usePermissions();
  const isClient = (role || "").toLowerCase() === "client";
  const isField = (role || "").toLowerCase() === "field_worker";
  const [tab, setTab] = useState(isClient ? "overview" : "register");

  if (isField) {
    return (
      <AppLayout title="Asset Register" subtitle="Search and view asset information" actions={<ClientSelector />}>
        {loading ? <div className="h-64 bg-white border border-slate-200 rounded-lg shimmer" /> : <AssetRegister />}
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title="Capital Planning"
      subtitle={isClient
        ? `Asset intelligence + capital renewal planning for ${client || "your organisation"}`
        : "Asset intelligence + capital renewal planning across all clients"}
      actions={<ClientSelector />}
    >
      {loading ? (
        <div className="h-64 bg-white border border-slate-200 rounded-lg shimmer" />
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-slate-200 p-1 h-auto flex flex-wrap justify-start gap-0.5 mb-6 w-full">
            <TabsTrigger data-testid="capital-tab-register" value="register" className="data-[state=active]:bg-gbe data-[state=active]:text-white rounded-md text-xs">Asset Register</TabsTrigger>
            <TabsTrigger data-testid="capital-tab-portfolio" value="portfolio" className="data-[state=active]:bg-gbe data-[state=active]:text-white rounded-md text-xs">Portfolio View</TabsTrigger>
            <TabsTrigger data-testid={TID.capitalTabOverview} value="overview" className="data-[state=active]:bg-slate-100 rounded-md text-xs">Client Overview</TabsTrigger>
            <TabsTrigger data-testid={TID.capitalTabRenewal} value="renewal" className="data-[state=active]:bg-slate-100 rounded-md text-xs">Renewal Assessment</TabsTrigger>
            <TabsTrigger data-testid={TID.capitalTabImmediate} value="immediate" className="data-[state=active]:bg-rose-50 data-[state=active]:text-rose-700 rounded-md text-xs">Beyond Design Life</TabsTrigger>
            <TabsTrigger data-testid={TID.capitalTab12M} value="12m" className="data-[state=active]:bg-amber-50 data-[state=active]:text-amber-700 rounded-md text-xs">12 Month</TabsTrigger>
            <TabsTrigger data-testid={TID.capitalTab24M} value="24m" className="data-[state=active]:bg-blue-50 data-[state=active]:text-gbe rounded-md text-xs">24 Month</TabsTrigger>
            <TabsTrigger data-testid={TID.capitalTabDeferred} value="deferred" className="data-[state=active]:bg-slate-100 rounded-md text-xs">Deferred Risk</TabsTrigger>
            <TabsTrigger data-testid={TID.capitalTabScenarios} value="scenarios" className="data-[state=active]:bg-slate-100 rounded-md text-xs">Scenarios</TabsTrigger>
            <TabsTrigger data-testid={TID.capitalTabAssetClass} value="class" className="data-[state=active]:bg-slate-100 rounded-md text-xs">Asset Class</TabsTrigger>
            <TabsTrigger data-testid={TID.capitalTabSite} value="site" className="data-[state=active]:bg-slate-100 rounded-md text-xs">Site Analysis</TabsTrigger>
            <TabsTrigger data-testid={TID.capitalTabForecast} value="forecast" className="data-[state=active]:bg-slate-100 rounded-md text-xs">10-Year Forecast</TabsTrigger>
            <TabsTrigger data-testid={TID.capitalTabBoard} value="board" className="data-[state=active]:bg-slate-100 rounded-md text-xs">Board Report</TabsTrigger>
            <TabsTrigger data-testid="capital-tab-askai" value="askai" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white rounded-md text-xs">Ask AI</TabsTrigger>
            <TabsTrigger data-testid="capital-tab-prioryear" value="prioryear" className="data-[state=active]:bg-slate-100 rounded-md text-xs">Prior Year</TabsTrigger>
            {!isClient && (
              <TabsTrigger data-testid="capital-tab-methodology" value="methodology" className="data-[state=active]:bg-violet-50 data-[state=active]:text-violet-700 rounded-md text-xs">Methodology</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="register"><AssetRegister /></TabsContent>
          <TabsContent value="portfolio"><AssetPortfolio /></TabsContent>
          <TabsContent value="methodology"><MethodologyTab /></TabsContent>
          {client ? (
            <>
              <TabsContent value="overview"><CapitalOverview /></TabsContent>
              <TabsContent value="renewal"><RenewalAssessment /></TabsContent>
              <TabsContent value="immediate"><RenewalAssessment fixedHorizon={HORIZONS.immediate} title="Beyond Design Life" tone="rose" /></TabsContent>
              <TabsContent value="12m"><RenewalAssessment fixedHorizon={HORIZONS.m12} title="12 Month Plan" tone="amber" /></TabsContent>
              <TabsContent value="24m"><RenewalAssessment fixedHorizon={HORIZONS.m24} title="24 Month Plan" tone="blue" /></TabsContent>
              <TabsContent value="deferred"><RenewalAssessment fixedHorizon={HORIZONS.monitor} title="Deferred Risk Register" tone="slate" /></TabsContent>
              <TabsContent value="scenarios"><Scenarios /></TabsContent>
              <TabsContent value="prioryear"><PriorYearComparison /></TabsContent>
              <TabsContent value="askai"><AskAssetAI /></TabsContent>
              <TabsContent value="class"><AssetClassForecast /></TabsContent>
              <TabsContent value="site"><SiteAnalysis /></TabsContent>
              <TabsContent value="forecast"><TenYearForecast /></TabsContent>
              <TabsContent value="board"><BoardReport /></TabsContent>
            </>
          ) : (
            tab !== "register" && tab !== "portfolio" && tab !== "methodology" && (
              <div className="text-center text-slate-500 py-16 text-sm">
                Pick a client from the top-right selector to view this tab, or use <b>Asset Register</b> / <b>Portfolio View</b> for a cross-client view.
              </div>
            )
          )}
        </Tabs>
      )}
    </AppLayout>
  );
}
